import { initLazyScript } from '../utils/theme-functions';

/**
 * Mega Menu Component
 * 
 * Creates a full-width dropdown menu with hover-based navigation.
 * Features left sidebar with categories and right content panel.
 * 
 * Required HTML structure:
 * - data-mega-menu-trigger: Trigger element
 * - data-mega-menu-dropdown: Main dropdown container
 * - data-mega-menu-child="handle": Child navigation items
 * - data-mega-menu-grandchildren="handle": Content panels
 */

const SELECTORS = {
    dropdown: '[data-mega-menu-dropdown]',
    trigger: '[data-mega-menu-trigger]',
    childLink: '[data-mega-menu-child]',
    grandchildren: '[data-mega-menu-grandchildren]'
};

const CSS_CLASSES = {
    hidden: 'js-hidden',
    active: 'bg-white font-semibold'
};

class MegaMenu extends HTMLElement {
    constructor() {
        super();
        this.currentActiveChild = null;
        this.currentActivePanel = null;
        initLazyScript(this, this.init.bind(this));
    }

    init() {
        this.dropdown = this.querySelector(SELECTORS.dropdown);
        this.trigger = this.querySelector(SELECTORS.trigger);
        this.childLinks = this.querySelectorAll(SELECTORS.childLink);
        this.grandchildContainers = this.querySelectorAll(SELECTORS.grandchildren);
        
        if (!this.dropdown || !this.trigger || this.childLinks.length === 0) {
            console.warn('MegaMenu: Required elements not found');
            return;
        }
        
        this.setupEventListeners();
        this.initializeDefaultState();
    }

    setupEventListeners() {
        // Handle child link hover to show/hide grandchildren
        this.childLinks.forEach(childLink => {
            const targetId = childLink.dataset.megaMenuChild;
            const grandchildContainer = this.querySelector(`[data-mega-menu-grandchildren="${targetId}"]`);
            
            if (grandchildContainer) {
                childLink.addEventListener('mouseenter', () => {
                    this.showPanel(childLink, grandchildContainer);
                });
            }
        });

        // Reset to first item when leaving the entire dropdown
        this.dropdown.addEventListener('mouseleave', () => {
            this.initializeDefaultState();
        });

        // Update ARIA attributes on hover
        this.addEventListener('mouseenter', () => {
            this.trigger.setAttribute('aria-expanded', 'true');
        });

        this.addEventListener('mouseleave', () => {
            this.trigger.setAttribute('aria-expanded', 'false');
        });
    }

    showPanel(childLink, grandchildContainer) {
        // Early return if same panel is already active
        if (this.currentActivePanel === grandchildContainer) {
            return;
        }

        try {
            // Hide current active panel
            if (this.currentActivePanel) {
                this.currentActivePanel.classList.add(CSS_CLASSES.hidden);
            }

            // Remove active state from current child
            if (this.currentActiveChild) {
                this.currentActiveChild.classList.remove(...CSS_CLASSES.active.split(' '));
                this.currentActiveChild.setAttribute('aria-selected', 'false');
                this.currentActiveChild.setAttribute('tabindex', '-1');
            }

            // Show new panel
            grandchildContainer.classList.remove(CSS_CLASSES.hidden);
            
            // Set new active child
            childLink.classList.add(...CSS_CLASSES.active.split(' '));
            childLink.setAttribute('aria-selected', 'true');
            childLink.setAttribute('tabindex', '0');

            // Update tracking
            this.currentActiveChild = childLink;
            this.currentActivePanel = grandchildContainer;
        } catch (error) {
            console.error('MegaMenu: Error showing panel', error);
        }
    }

    initializeDefaultState() {
        if (this.childLinks.length > 0 && this.grandchildContainers.length > 0) {
            const firstChildLink = this.childLinks[0];
            const firstGrandchildContainer = this.grandchildContainers[0];
            
            // Show first panel immediately to ensure something is always visible
            firstGrandchildContainer.classList.remove(CSS_CLASSES.hidden);
            
            // Reset all other panels to hidden
            this.grandchildContainers.forEach((container, index) => {
                if (index !== 0) {
                    container.classList.add(CSS_CLASSES.hidden);
                }
            });

            // Reset all child links
            this.childLinks.forEach((link, index) => {
                if (index === 0) {
                    // Set first as active
                    link.classList.add(...CSS_CLASSES.active.split(' '));
                    link.setAttribute('aria-selected', 'true');
                    link.setAttribute('tabindex', '0');
                } else {
                    // Reset others
                    link.classList.remove(...CSS_CLASSES.active.split(' '));
                    link.setAttribute('aria-selected', 'false');
                    link.setAttribute('tabindex', '-1');
                }
            });
            
            // Update tracking
            this.currentActiveChild = firstChildLink;
            this.currentActivePanel = firstGrandchildContainer;
        }
    }
}

customElements.define('mega-menu', MegaMenu);